/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {DialogModule} from "@intuitionrobotics/thunderstorm/frontend";
import {FONTS} from "@res/fonts";
import {DialogCard} from "../../widget/DialogCard";
import {RemindersModule} from "@modules/RemindersModule";
import {DB_Patient} from "@app/app-shared/patient";
import {Reminder} from "@app/app-shared/reminder";
import {MessagingModule} from "@modules/MessagingModule";
import {ScheduledBy} from "@app/ir-q-app-common/types/calendar_event";
import {ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {DB_MessageStatus, MessageMap, MessageStatuses} from "@app/ir-q-app-common/types/message";
import {CCInbox} from "@app/ir-q-app-common/types/message-api";
import {ChatsContext, ChatsContextType} from "../messages/ChatsContext";
import {OrganizationsContext} from "../organizations/OrganizationsContext";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";
import {ContactsContext} from "../messages/ContactsContext";


type Props = {
    patient: DB_Patient
    reminder: Reminder
}

export const DeleteReminderDialog = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const orgCtx = useContext(OrganizationsContext);
    const cCtx = useContext(ChatsContext);
    const {contacts} = useContext(ContactsContext);

    const closeListener = (e: KeyboardEvent) => {
        if (e.key === "Escape")
            DialogModule.close();
    }

    useEffect(() => {
        window.addEventListener('keydown', closeListener)
        return () => {
            window.removeEventListener('keydown', closeListener)
        }
    }, []);

    const getGroupSender = () => {
        const patient = props.patient;
        const organizationData = RemindersModule.getPatientsOrganizationAndContacts(contacts, orgCtx.organizations[patient.organizationId]);
        if (!organizationData) {
            DialogModule.close();
            return {};
        }

        // We select the clinic contact if the organization is clinical, else the customer service one
        const contact = organizationData.type === ORGANIZATION_TYPE.INTERNAL ? organizationData.contacts.customerServiceContact : organizationData.contacts.orgContact;
        if (!contact) {
            DialogModule.close();
            return {};
        }

        const contactName = organizationData.type === ORGANIZATION_TYPE.INTERNAL ? `${contact.firstName} ${contact.lastName}` : contact.firstName

        return {contact, contactName};
    }

    const onReminderDelete = () => {
        if (!getIfNeedToSendMessage()) {
            DialogModule.close();
            return
        }

        const {contact, contactName} = getGroupSender();

        if (!contact || !contactName) {
            DialogModule.close();
            return;
        }

        const patient = props.patient;
        const chat = Object.values(cCtx.chats || {}).find((_chat: CCInbox) => [contact._id, props.patient.contactId].every(contactId => _chat.contactIds.includes(contactId)));

        if (chat)
            cCtx.setIsSendingToChat(chat._id, true)

        const text = `${contactName} has removed the following medication reminder: ${props.reminder.subject}`;
        const data = {text};

        MessagingModule.send(text,
            contact._id,
            [patient.contactId])
            .catch(error => {
                if (!chat)
                    return;

                onSendMessage(false, cCtx, chat, contact._id, data);
            }).then(async () => {
            if (!chat) {
                return;
            }

            onSendMessage(true, cCtx, chat, contact._id, data);
        }).catch(console.error);

        DialogModule.close();
    }

    const onSendMessage = (success: boolean, {messages, saveChats, saveMessages, setIsSendingToChat}: ChatsContextType, chat: CCInbox, senderId: string, data: MessageMap["text"]) => {
        setIsSendingToChat(chat._id, false);
        const message = MessagingModule.onSentMessageInExistingChat(chat, senderId, data, "text");
        const updateStatus: DB_MessageStatus = {...chat.lastMessage.status, status: success ? MessageStatuses.SENT : MessageStatuses.FAILED};
        chat.lastMessage = {...message, status: updateStatus};
        saveChats([chat])

        if (messages?.[chat._id]) {
            saveMessages([{...message, status: updateStatus}]);
        }
    }

    const getIfNeedToSendMessage = () => {
        return props.reminder.status === 'CREATED' && props.reminder.originator === ScheduledBy.USER;
    }


    const content = `Are you sure you want to delete ${props.reminder.subject}?${!getIfNeedToSendMessage() ? "" : `\n\nA message will be sent to ${props.patient.name} notifying them that reminder was removed.`}`;

    return <DialogCard
        style={{width: 500, whiteSpace: "break-spaces"}}
        showX={true}
        title={"Delete reminder?"}
        content={FONTS.reg_16_secondary_dark(content)}
        saveLabel={"Delete"}
        cancelLabel={"Cancel"}
        onSave={() => {
            setLoading(true);
            RemindersModule.deleteReminder(props.patient, props.reminder.id).then(() => {
                AnalyticsModule.logCareManagerEvent({
                    objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
                    entity: AnalyticConsts.Entities.USER,
                    activityStatus: AnalyticConsts.ActivityStatus.DELETE,
                    objectSubType: AnalyticConsts.ObjectSubTypes.CARE_PLAN,
                    activityDetails: AnalyticConsts.ActivityDetails.MEDICATION_REMINDER,
                    objectId: props.reminder.id.toString()
                });

                onReminderDelete();
                RemindersModule.fetchRemindersForPatient(contacts, orgCtx.organizations[props.patient.organizationId], props.patient);
            }).catch(console.error);

            return true;
        }}
        loading={loading}
    />;
}
